import { isDefined, isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import { Amount } from 'components/shared/Amount/Amount';
import { useCallback, useState } from 'react';
import type { SecondaryPriceType } from 'state/secondaryMarket/productSearch';

export interface CompactNumberInputProps {
  value: number | null | undefined;
  onValueChanged: (val?: number) => void;
  priceType: SecondaryPriceType;
  hasStep?: boolean;
  disabled?: boolean;
  precision?: number;
}

export function CompactNumberInput({
  value,
  onValueChanged,
  priceType,
  hasStep = false,
  disabled = false,
  precision,
}: CompactNumberInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const formatValue = useCallback(
    (v: number | undefined | null) => {
      if (isNotDefined(v) || v === null || isNaN(v)) {
        return undefined;
      }
      const computedValue = priceType === 'Percent' ? v * 100 : v;
      return computedValue;
    },
    [priceType],
  );

  const onChange = useCallback(
    (value?: number) => {
      if (isDefined(value) && !isNaN(value)) {
        onValueChanged(priceType === 'Percent' ? value / 100.0 : value);
      } else {
        onValueChanged(undefined);
      }
    },
    [onValueChanged, priceType],
  );

  const step = priceType === 'Amount' ? 1 : 0.1;

  return (
    <Amount
      className={classNames('form-control', {
        'fs-6': !isEditing,
        'input-number-without-step': !hasStep,
      })}
      value={formatValue(value)}
      precision={precision}
      onChange={onChange}
      onIsEditingChange={v => setIsEditing(v)}
      isDisabled={disabled}
      incrementStep={hasStep ? step : undefined}
    />
  );
}
